import React from 'react';

type TFn = (key: string, def: string) => string;

export function flightStatusMeta(status: string, f: any, t: TFn): { cls: string; statusTxt: string; suffix: string } {
  const s = (status || '').toLowerCase();
  let cls = 'bg-blue-50 text-blue-700 border-blue-200';
  let statusTxt = '';
  if (s === 'landed' || s === 'arrived' || s === 'on_time' || s === 'on time') {
    cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    statusTxt = s.startsWith('on') ? t('flights.onTime', 'I rute') : t('flights.landed', 'Landet');
  } else if (s.includes('delay')) {
    cls = 'bg-amber-100 text-amber-700 border-amber-200';
    statusTxt = `${t('flights.delayed', 'Forsinket')} +${f.delay_minutes || '?'} min${f.new_time ? ` (${t('flights.newTime', 'ny tid')} ${f.new_time})` : ''}`;
  } else if (s === 'cancelled') {
    cls = 'bg-red-100 text-red-700 border-red-200';
    statusTxt = t('flights.cancelled', 'KANSELLERT');
  } else if (s === 'departed') {
    cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    statusTxt = t('flights.departed', 'Avreist');
  }
  const suffix = `${s.includes('delay') && (f.delay_minutes || 0) > 0 ? ` +${f.delay_minutes}m` : ''}${s === 'cancelled' ? ' ✕' : ''}`;
  return { cls, statusTxt, suffix };
}

/** Flynr-kolonnen: ett merke per ekte flynummer (tid-lignende «numre» er flyttet til Flytid av backend). */
export function FlightNumberChips({ trip, t }: { trip: any; t: TFn }) {
  const flights: any[] = trip.flights?.length ? trip.flights : (trip.flight_numbers || []).map((n: string) => ({ flight_number: n }));
  if (!flights.length) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-0.5">
      {flights.map((f: any, i: number) => {
        const m = flightStatusMeta(f.flight_status, f, t);
        return (
          <span key={i} title={`${f.flight_number}${f.arrival_time ? ` · ${f.arrival_time}` : ''}${m.statusTxt ? ` — ${m.statusTxt}` : ''}`}
            className={`inline-flex items-center px-1 py-0 rounded border text-[9px] font-semibold leading-tight ${m.cls}`} data-testid={`flight-no-chip-${trip.trip_number}-${i}`}>
            {f.flight_number}{m.suffix}
          </span>
        );
      })}
    </span>
  );
}

/** Flytid-kolonnen: ett merke per unik tid (inkl. tider importert uten flynummer, f.eks. «05:50 BGO»). */
export function FlightTimeChips({ trip, t }: { trip: any; t: TFn }) {
  const times: any[] = trip.flight_times?.length ? trip.flight_times
    : (trip.flights || []).filter((f: any) => f.arrival_time).map((f: any) => ({ time: f.arrival_time, flight_numbers: [f.flight_number], flight_status: f.flight_status, delay_minutes: f.delay_minutes, new_time: f.new_time }));
  if (!times.length) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-0.5">
      {times.map((x: any, i: number) => {
        const m = flightStatusMeta(x.flight_status, x, t);
        const label = `${x.time}${x.airport ? ` ${x.airport}` : ''}`;
        return (
          <span key={i} title={`${label}${x.flight_numbers?.length ? ` · ${x.flight_numbers.join(', ')}` : ''}${m.statusTxt ? ` — ${m.statusTxt}` : ''}`}
            className={`inline-flex items-center px-1 py-0 rounded border text-[9px] font-mono font-semibold leading-tight ${m.cls}`} data-testid={`flight-time-chip-${trip.trip_number}-${i}`}>
            {label}{m.suffix}
          </span>
        );
      })}
    </span>
  );
}

/** Ren tekst for eksport/PDF/e-post: [Flynr, Flytid] — Flytid med «+Nm»/«✕» som i UI-merkene. */
export function flightTextColumns(trip: any): [string, string] {
  const nums: string[] = trip.flights?.length ? trip.flights.map((f: any) => f.flight_number) : (trip.flight_numbers || []);
  const suffix = (x: any) => {
    const s = String(x.flight_status || '').toLowerCase();
    return s.includes('delay') && x.delay_minutes ? ` +${x.delay_minutes}m` : s === 'cancelled' ? ' ✕' : '';
  };
  const times: string[] = trip.flight_times?.length
    ? trip.flight_times.map((x: any) => `${x.time}${x.airport ? ` ${x.airport}` : ''}${suffix(x)}`)
    : (trip.flights || []).filter((f: any) => f.arrival_time || f.local_time).map((f: any) => `${f.arrival_time || f.local_time}${suffix(f)}`);
  return [nums.filter(Boolean).join(', '), times.filter(Boolean).join(', ')];
}
